/**
 * What the POS does, and how each of it is undone.
 *
 * Kept as data rather than markup so the page can filter it, and so the reversal column is
 * impossible to leave out when an action is added — every entry has to answer the question.
 */

import { msg } from '@/i18n'

/** How an action can be taken back. */
export type Undo =
  | {
      /** Reversed by an admin from a screen in the app. */
      kind: 'reversible'
      /** Where the button is. */
      where: string
      /** What putting it back actually moves. */
      effect: string
      /** When the app will refuse. */
      refusedWhen?: string
    }
  | {
      /** Corrected by doing something else, not by an undo button. */
      kind: 'corrected'
      how: string
    }
  | {
      /** Genuinely permanent. The reason is the important part. */
      kind: 'permanent'
      why: string
    }

export interface Action {
  name: string
  /** What it does, in a sentence. */
  what: string
  /** Who is allowed. */
  who: string
  /** Where in the app. */
  where: string
  undo: Undo
}

export interface Section {
  id: string
  title: string
  blurb: string
  actions: Action[]
}

export const GUIDE: Section[] = [
  {
    id: 'shifts',
    title: msg('Tills and shifts'),
    blurb:
      msg('A shift is one cashier at one register, from the float they counted in to the cash they counted out. Nothing can be sold without one open, because an uncounted opening float makes the closing count meaningless.'),
    actions: [
      {
        name: msg('Open a shift'),
        what: msg('Starts a till. It opens empty — money reaches a drawer from the safe, so that every note in it came from somewhere the shop can point at, and so the float sits behind the same permission as the safe rather than behind a number anyone could type.'),
        who: msg('Anyone who can reach the checkout.'),
        where: msg('Checkout, or Cash → Drawer.'),
        undo: {
          kind: 'corrected',
          how: msg('Close it and open another. Nothing has been sold yet, so nothing is lost.'),
        },
      },
      {
        name: msg('Close a shift'),
        what: msg('Counts the drawer, freezes what the system expected, and records the difference. The Z-report shows every step from the empty till to now — the float drawn, the cash taken, the change given, each pay-out by name — with the running balance in both currencies, so the figure being counted against can be checked rather than trusted. Dollars and lira are counted separately, and both have to be entered — put 0 if there is none of one. A drawer that comes up short or over cannot be closed without a note saying why, because this is the last moment anyone can explain it.'),
        who: msg('Close shift permission — managers and the owner.'),
        where: msg('Cash → Drawer → Close.'),
        undo: {
          kind: 'permanent',
          why: msg('The drawer was physically counted and the figure agreed. Reopening it would let the count be changed after the fact, which is exactly what the count exists to prevent. Anything that needs correcting is recorded against the shift that is open now.'),
        },
      },
      {
        name: msg('Pay in / pay out'),
        what: msg('Moves cash into or out of the drawer for something that is not a sale. Anything taking money out is refused if the till does not hold it — dollars and lira separately, since a drawer full of dollars still cannot pay out lira.'),
        who: msg('Anyone on the till.'),
        where: msg('Cash → Drawer.'),
        undo: {
          kind: 'reversible',
          where: msg('Cash → Drawer, the ↺ button on the movement.'),
          effect: msg('Writes the opposite movement, so the expected drawer figure returns to what it was.'),
          refusedWhen:
            msg('The movement is the cash side of an expense or an account payment — reverse that instead and the cash comes back with it.'),
        },
      },
      {
        name: msg('Draw change from the safe / drop to safe'),
        what: msg('Moves cash between a till and the branch safe, in either direction. Drawing from the safe is how a drawer gets its float, since a till opens empty. Both sides are written together as one movement, so the safe can never issue money the drawer does not receive.'),
        who: msg('Manage safe permission.'),
        where: msg('Cash → Drawer.'),
        undo: {
          kind: 'reversible',
          where: msg('Cash → Drawer, the ↺ button on the movement.'),
          effect: msg('Reverses both sides together — the drawer and the safe — so the two cannot end up disagreeing.'),
        },
      },
    ],
  },
  {
    id: 'selling',
    title: msg('Selling'),
    blurb:
      msg('Scan or search, take payment in dollars, lira or both, and print. A sale snapshots its own prices, VAT and costs, so a receipt reprinted next year still shows what the customer actually paid.'),
    actions: [
      {
        name: msg('Ring up a sale'),
        what: msg('Takes payment, drops stock from the oldest-expiring batches first, and prints a receipt.'),
        who: msg('Anyone on the till.'),
        where: msg('Checkout.'),
        undo: {
          kind: 'corrected',
          how: msg('Void it if it should never have happened, or refund it if goods came back. Both are on the Sales screen. Which one you use matters: a void says the sale did not happen, a refund says it did and was returned.'),
        },
      },
      {
        name: msg('Sell by weight'),
        what: msg('For scale items, asks for a weight — or for an amount of money and works out the weight. Both are calculated on the server.'),
        who: msg('Anyone on the till.'),
        where: msg('Checkout, when the item is a weighed one.'),
        undo: {
          kind: 'corrected',
          how: msg('Remove the line before paying, or refund the sale afterwards.'),
        },
      },
      {
        name: msg('Put a sale on account'),
        what: msg('Lets a known customer take goods and pay later. Needs a customer, and cannot exceed their credit limit.'),
        who: msg('Anyone on the till. The credit limit is what decides, not a separate permission.'),
        where: msg('Checkout → Payment → On account.'),
        undo: {
          kind: 'corrected',
          how: msg('Void or refund the sale. The charge on the customer’s account is cancelled with it. The ledger row itself cannot be reversed on its own — that would leave the sale claiming it was paid.'),
        },
      },
      {
        name: msg('Hold and resume a sale'),
        what: msg('Parks a cart so the next customer can be served, and brings it back later.'),
        who: msg('Anyone on the till.'),
        where: msg('Checkout.'),
        undo: { kind: 'corrected', how: msg('Discard the held cart. Nothing has been sold, so nothing has moved.') },
      },
      {
        name: msg('Discount a line or a whole sale'),
        what: msg('Reduces the price. A whole-sale discount is spread across the lines by value before VAT is worked out.'),
        who: msg('Discount permission.'),
        where: msg('Checkout.'),
        undo: { kind: 'corrected', how: msg('Change it before taking payment; afterwards, refund the sale.') },
      },
    ],
  },
  {
    id: 'reversals',
    title: msg('Refunds and voids'),
    blurb:
      msg('Two different remedies for two different things. A void says the sale should never have been recorded. A refund says it was, and goods came back.'),
    actions: [
      {
        name: msg('Void a sale'),
        what: msg('Reverses a sale whole: stock returns to the exact batches it left, and the cash comes out of today’s drawer.'),
        who: msg('Void permission.'),
        where: msg('Sales → the ⃠ button.'),
        undo: {
          kind: 'permanent',
          why: msg('A void is itself the reversal. Undoing it would mean re-charging a customer who has already been given their money back. If the sale should stand, ring it up again — the receipt number of the void stays used either way.'),
        },
      },
      {
        name: msg('Refund items'),
        what: msg('Gives money back for some or all of a sale, at the price actually paid. Returned goods either go back on the shelf or are written off.'),
        who: msg('Refund permission.'),
        where: msg('Sales → the ↺ button.'),
        undo: {
          kind: 'reversible',
          where: msg('Sales → Refunds tab → the ↺ button.'),
          effect:
            msg('Takes the goods back off the shelf, puts the money back in the drawer, cancels any account credit, and returns the sale to the state it was in before.'),
          refusedWhen:
            msg('The returned goods have since been sold again. Ring the difference up as a sale instead.'),
        },
      },
    ],
  },
  {
    id: 'stock',
    title: msg('Stock'),
    blurb:
      msg('Stock on hand is the sum of its batches — there is no quantity anyone can type over. Every batch carries the cost it arrived at, which is what makes profit trustworthy.'),
    actions: [
      {
        name: msg('Print barcode labels for a delivery'),
        what: msg('After a delivery is posted, its items are listed with a copies box already set to how many came in. Anything with no barcode yet gets one minted here. Past deliveries reopen for reprinting from the list at the bottom.'),
        who: msg('Manage stock permission.'),
        where: msg('Inventory → Add stock.'),
        undo: {
          kind: 'corrected',
          how: msg('Print again. Reopening a past delivery shows only its labels — the delivery itself is a record of what the goods cost and cannot be edited.'),
        },
      },
      {
        name: msg('Choose how your barcodes are numbered'),
        what: msg('Full EAN-13 for a shop that scans, or short numbers — 0001, 0002 — for a shop that keys the code in by hand. Running past the width is fine: 9999 is followed by 10000 and old labels keep working.'),
        who: msg('Manage settings permission.'),
        where: msg('Settings → Barcodes.'),
        undo: {
          kind: 'corrected',
          how: msg('Change it back. Codes already printed keep working either way, and the counter carries on from the highest number already issued rather than restarting.'),
        },
      },
      {
        name: msg('Receive a delivery'),
        what: msg('Creates a draft, then posts it: batches are created, freight is spread across the lines by value, and costs are fixed. Posting is also when the money leaves — say whether it comes out of the till, the safe, the bank, a card, or stays on credit. Paying from a till or the safe that cannot cover it is refused, and no stock lands. Buying stock is never an expense: it is an asset until it sells, and it reaches the profit and loss as cost of sales then, so counting it twice would make every delivery day look like a loss.'),
        who: msg('Manage stock permission.'),
        where: msg('Inventory → Add stock.'),
        undo: {
          kind: 'reversible',
          where: msg('Inventory → Deliveries → the ↺ button.'),
          effect: msg('Takes the goods back out of stock and marks the delivery cancelled.'),
          refusedWhen:
            msg('Any of it has already been sold. Unposting then would leave sales whose cost came from batches that no longer claim to exist. Write off what is left instead, or correct the cost on a new delivery.'),
        },
      },
      {
        name: msg('Pay a delivery taken on credit'),
        what: msg('Settles what is still owed to a supplier, out of the till, the safe, the bank or a card. Until then it shows in what you owe. The cash leaves exactly as it would have at the door — the only difference is when.'),
        who: msg('Manage stock permission.'),
        where: msg('Inventory → Deliveries.'),
        undo: {
          kind: 'corrected',
          how: msg('Reverse the drawer or safe movement it wrote, which puts the money back. A delivery cannot be settled twice — paying again would take the money out twice over.'),
        },
      },
      {
        name: msg('Edit a draft delivery'),
        what: msg('Changes lines, quantities and costs before anything moves.'),
        who: msg('Manage stock permission.'),
        where: msg('Inventory → Add stock.'),
        undo: { kind: 'corrected', how: msg('A draft has not touched stock. Edit it freely until you post it.') },
      },
      {
        name: msg('Write stock off'),
        what: msg('Takes stock off the shelf as expired, damaged, stolen, given away, used by the shop, or missing at a count. Valued at what it cost, never at what it would have sold for.'),
        who: msg('Record waste permission. Your name is kept against it.'),
        where: msg('Inventory → the 🗑 button on a product.'),
        undo: {
          kind: 'reversible',
          where: msg('Inventory → Written off → the ↺ button.'),
          effect:
            msg('Puts the goods back into the batch they came out of, so the expiry date and the cost survive. The loss drops out of the profit and loss.'),
        },
      },
      {
        name: msg('Create or edit a product'),
        what: msg('Sets the name, price, VAT rate, unit, whether it is weighed, and whether it tracks expiry. Add it inside the delivery that brings it in and its cost is real from the start; add it on its own — to price a shelf or print labels before anything arrives — and the cost stays a placeholder until the first delivery refreshes it. A missing category is added right beside the list it was missing from. Editing an existing product is also where each batch on the shelf gets its expiry date corrected.'),
        who: msg('Manage catalogue permission.'),
        where: msg('Inventory → New product, Inventory → Add stock → New product, or tap an existing product to edit it.'),
        undo: {
          kind: 'corrected',
          how: msg('Edit it back, or mark it inactive. Products are never deleted, because sales history points at them.'),
        },
      },
      {
        name: msg('Give a product a barcode and print labels'),
        what: msg('Mints a code that cannot collide with a real manufacturer’s barcode, or accepts one you type, and prints labels for a label printer.'),
        who: msg('Manage catalogue permission.'),
        where: msg('Inventory → the barcode button on a product.'),
        undo: { kind: 'corrected', how: msg('Remove the barcode from the product and add the right one.') },
      },
    ],
  },
  {
    id: 'cash',
    title: msg('The safe and the owner’s money'),
    blurb:
      msg('The safe balance is summed from its own history, never stored, so it cannot drift away from the rows that produced it. It can never go below zero — there is either a note in it or there is not.'),
    actions: [
      {
        name: msg('Take money out for yourself'),
        what: msg('Records an owner drawing. It appears in the profit and loss below net profit, alongside what is left in the business.'),
        who: msg('Manage safe permission.'),
        where: msg('Cash → Safe → Take out profit.'),
        undo: {
          kind: 'reversible',
          where: msg('Cash → Safe → the ↺ button on the transaction.'),
          effect: msg('Puts the money back and removes it from the drawings figure.'),
          refusedWhen: msg('The safe no longer holds enough to put it back.'),
        },
      },
      {
        name: msg('Put money in'),
        what: msg('Records an owner injection. It is not income, and never reaches the profit figure.'),
        who: msg('Manage safe permission.'),
        where: msg('Cash → Safe.'),
        undo: {
          kind: 'reversible',
          where: msg('Cash → Safe → the ↺ button.'),
          effect: msg('Takes it back out again.'),
          refusedWhen: msg('The money has since been spent and the safe would go below zero.'),
        },
      },
      {
        name: msg('Bank deposit or a count correction'),
        what: msg('Records cash taken to the bank, or an adjustment after physically counting the safe.'),
        who: msg('Manage safe permission.'),
        where: msg('Cash → Safe.'),
        undo: {
          kind: 'reversible',
          where: msg('Cash → Safe → the ↺ button.'),
          effect: msg('Writes the opposite transaction, so the balance returns exactly.'),
        },
      },
    ],
  },
  {
    id: 'customers',
    title: msg('Customers and accounts'),
    blurb:
      msg('A customer’s balance is kept beside their ledger so a credit check at the till is instant, but the ledger is the authority. Both are written together, and a reconcile check proves they still agree.'),
    actions: [
      {
        name: msg('Add or edit a customer'),
        what: msg('Records who they are and what credit limit they have. The limit is what decides whether the till will let them charge.'),
        who: msg('Manage customers permission.'),
        where: msg('Customers.'),
        undo: { kind: 'corrected', how: msg('Edit it back, or set the credit limit to zero to stop further charges.') },
      },
      {
        name: msg('Take a payment against an account'),
        what: msg('Reduces what a customer owes. Cash goes into the open till so the drawer still reconciles.'),
        who: msg('Manage customers permission.'),
        where: msg('Customers → the customer → Take a payment.'),
        undo: {
          kind: 'reversible',
          where: msg('Customers → the customer → the ↺ button on the payment.'),
          effect:
            msg('Puts the debt back on the account and takes the cash back out of the till. The statement keeps both rows: the payment as it was, and the correction after it.'),
        },
      },
    ],
  },
  {
    id: 'expenses',
    title: msg('Expenses'),
    blurb:
      msg('Money out that is not stock. Goods bought are an asset and only become a cost when they sell, so a delivery never creates an expense — booking it as one would count the same money twice.'),
    actions: [
      {
        name: msg('Record an expense'),
        what: msg('Rent, wages, the generator bill. Paid from the till, the safe, the bank, a card, or left on credit as a payable.'),
        who: msg('Record expense permission.'),
        where: msg('Expenses → Record expense.'),
        undo: {
          kind: 'reversible',
          where: msg('Expenses → the ↺ button.'),
          effect:
            msg('Puts the money back where it came from — the till or the safe — and drops the cost out of the profit and loss. The expense stays on the list, struck through, with your reason against it.'),
        },
      },
      {
        name: msg('Mark an expense paid'),
        what: msg('Settles a bill that was recorded on credit.'),
        who: msg('Record expense permission.'),
        where: msg('Expenses → Mark paid.'),
        undo: { kind: 'corrected', how: msg('Reverse the whole expense and record it again as it should have been.') },
      },
    ],
  },
  {
    id: 'reports',
    title: msg('Reports'),
    blurb:
      msg('Every period is cut on the shop’s own days, not on UTC, so a sale rung up at 1 a.m. lands on the day the cashier was working. Voided sales are excluded from every figure, and reversed entries stop counting the moment they are reversed.'),
    actions: [
      {
        name: msg('Read a report'),
        what: msg('Daily and monthly sales, profit and loss, VAT, inventory, receivables, waste and voids.'),
        who: msg('View reports permission.'),
        where: msg('Reports.'),
        undo: { kind: 'corrected', how: msg('Reports only read. Nothing you do here changes a figure.') },
      },
      {
        name: msg('Build a custom report'),
        what: msg('Tick the columns you want; the report groups itself. Columns always appear in a fixed order, whatever order you ticked them in.'),
        who: msg('View reports permission.'),
        where: msg('Reports → Build.'),
        undo: { kind: 'corrected', how: msg('Change the ticks and run it again.') },
      },
      {
        name: msg('Save a report as a template'),
        what: msg('Keeps the columns, filters and dates you picked so the same report opens with one tap. Up to ten each, and they are yours — nobody else sees them. A template holds the question, never the answer, so it runs afresh every time you open it.'),
        who: msg('View reports permission.'),
        where: msg('Reports → Build → Save as template.'),
        undo: {
          kind: 'corrected',
          how: msg('Rename or delete it from the row of buttons. Deleting a template removes a shortcut and nothing else — no figure anywhere changes.'),
        },
      },
      {
        name: msg('Export to CSV'),
        what: msg('Downloads exactly what the screen is showing, so a file and a page can never disagree.'),
        who: msg('View reports permission.'),
        where: msg('The download button on any report.'),
        undo: { kind: 'corrected', how: msg('Delete the file. Exporting changes nothing.') },
      },
    ],
  },
  {
    id: 'settings',
    title: msg('Settings'),
    blurb: msg('The things that change how the whole shop behaves. Most are edits; two are deliberately not.'),
    actions: [
      {
        name: msg('Change the LBP rate'),
        what: msg('Sets the rate used to convert lira from now on. Every transaction keeps the rate it was made at.'),
        who: msg('Change exchange rate permission.'),
        where: msg('Settings → LBP rate.'),
        undo: {
          kind: 'corrected',
          how: msg('Enter the right rate as a new one. Rates are added, never edited, so yesterday’s takings can never be restated at today’s figure.'),
        },
      },
      {
        name: msg('Turn VAT on or off'),
        what: msg('For a shop below the registration threshold. Every line is then charged at 0%, but each product keeps its own rate, so switching back restores which goods were exempt.'),
        who: msg('Manage settings permission.'),
        where: msg('Settings → VAT.'),
        undo: {
          kind: 'corrected',
          how: msg('Switch it back. Old receipts are untouched — a receipt shows VAT only when that sale actually charged some.'),
        },
      },
      {
        name: msg('Add a user or change what they can do'),
        what: msg('Creates a cashier, manager or admin, and grants or removes individual rights.'),
        who: msg('Manage users permission.'),
        where: msg('Settings → Users.'),
        undo: { kind: 'corrected', how: msg('Change the permissions back, or deactivate the account.') },
      },
      {
        name: msg('Set a user’s password'),
        what: msg('Type a new password on the user, or leave the field blank and one is generated for you. Either way the old password stops working immediately.'),
        who: msg('Manage users permission.'),
        where: msg('Settings → Users → tap the person → New password.'),
        undo: {
          kind: 'permanent',
          why: msg('A generated password is created once, never stored, and cannot be reissued — write it down when it is shown to you, because that is the only time you will see it. Locking yourself out of the owner account is recoverable: whoever provides your system can issue a new password from their own panel, and the reset is recorded in your audit log.'),
        },
      },
      {
        name: msg('Add a branch'),
        what: msg('Opens another branch, up to the limit on your plan.'),
        who: msg('Manage settings permission.'),
        where: msg('Settings → Branches.'),
        undo: { kind: 'corrected', how: msg('Rename it, or stop using it. Branches are not deleted — trading history points at them.') },
      },
    ],
  },
]

/** Everything an admin can undo, for the summary table at the top of the page. */
export const REVERSIBLE_ACTIONS = GUIDE.flatMap((section) =>
  section.actions
    .filter((action) => action.undo.kind === 'reversible')
    .map((action) => ({ section: section.title, action })),
)
